import {createContext, type PropsWithChildren, useContext, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

interface CopilotAppContextType {
  copilotApp: string | null
  setCopilotApp: (copilotApp: string | null) => void
}

const CopilotAppContext = createContext<CopilotAppContextType | undefined>(undefined)

export function useCopilotApp() {
  const context = useContext(CopilotAppContext)
  if (!context) throw new Error('useCopilotApp must be used within a CopilotAppProvider')
  return context
}

export function CopilotAppProvider({children}: PropsWithChildren) {
  const hideCopilot = useFeatureFlag('copilot_extensions_removal_on_marketplace')
  const [searchParams] = useSearchParams()
  const [internalCopilotApp, setInternalCopilotApp] = useState<string | null>(() =>
    hideCopilot ? null : searchParams.get('copilot_app'),
  )
  const copilotApp = hideCopilot ? null : internalCopilotApp

  const value = useMemo(
    () => ({
      copilotApp,
      setCopilotApp: (newValue: string | null) => setInternalCopilotApp(hideCopilot ? null : newValue),
    }),
    [copilotApp, hideCopilot],
  )
  return <CopilotAppContext value={value}>{children}</CopilotAppContext>
}

try{ CopilotAppContext.displayName ||= 'CopilotAppContext' } catch {}
try{ CopilotAppProvider.displayName ||= 'CopilotAppProvider' } catch {}